// This file is currently not checked by eslint.
// When you work on this file, please remove its entries from .eslintignore

import React, { Component } from 'react';
import PropTypes from 'prop-types';
import invariant from 'invariant';
import { camelize } from './../utils/String';
import GoogleApi from './../utils/GoogleApi';

const mapStyles = {
  container: {
    position: 'absolute',
    width: '100%',
    height: '100%',
  },
  map: {
    position: 'absolute',
    left: 0,
    right: 0,
    bottom: 0,
    top: 0,
  },
};

const centerDefault = {
  lat: '51.165691',
  lng: '10.451526',
};

export { Marker } from './Marker';
export { InfoWindow } from './InfoWindow';

export default class GMap extends Component {
  constructor(props) {
    super(props);

    invariant(
      Object.prototype.hasOwnProperty.call(props, 'google'),
      'You must include a `google` prop.',
    );

    this.listeners = {};
    this.state = {
      centerMap: null,
      isMapCreated: false,
    };

    this.mapsCallback = this.mapsCallback.bind(this);
  }

  componentDidMount() {
    this.setState({
      callbackIndex: GoogleApi.load(this.props.params, this.mapsCallback),
    });
    if (this.props.centerAroundCurrentLocation) {
      if (navigator && navigator.geolocation) {
        this.geoPromise = makeCancelable(
          new Promise((resolve, reject) => {
            navigator.geolocation.getCurrentPosition(resolve, reject);
          }),
        );

        this.geoPromise.promise
          .then((pos) => {
            const coords = pos.coords;
            this.setState({
              centerMap: {
                lat: coords.latitude,
                lng: coords.longitude,
              },
            });
          })
          .catch((e) => e);
      }
    }
    this.boundingClientRect = this.mapElement && this.mapElement.getBoundingClientRect();
  }

  componentDidUpdate(prevProps, prevState) {
    if (this.props.visible !== prevProps.visible) {
      this.restyleMap();
    }
    if (this.props.zoom !== prevProps.zoom) {
      this.map.setZoom(this.props.zoom);
    }
    if (this.state.centerMap !== prevState.centerMap) {
      this.recenterMap();
    }
    const clientRect = this.mapElement && this.mapElement.getBoundingClientRect();
    if (
      this.map &&
      this.boundingClientRect &&
      (this.boundingClientRect.height !== clientRect.height ||
        this.boundingClientRect.width !== clientRect.width)
    ) {
      this.boundingClientRect = clientRect;
      window.google.maps.event.trigger(this.map, 'resize');
    }
  }

  componentWillUnmount() {
    GoogleApi.removeCallback(this.state.callbackIndex);
  }

  createMap() {
    if (this.props && window.google) {
      const google = window.google;

      const mapRef = this.mapElement;
      const curr = centerDefault;
      const center = new google.maps.LatLng(curr.lat, curr.lng);

      const mapTypeIds = google.maps.MapTypeId || {};
      const mapTypeFromProps = String(this.props.mapType).toUpperCase();

      const mapConfig = Object.assign(
        {},
        {
          mapTypeId: mapTypeIds[mapTypeFromProps],
          center,
          zoom: this.props.zoom,
          maxZoom: this.props.maxZoom,
          minZoom: this.props.maxZoom,
          clickableIcons: this.props.clickableIcons,
          disableDefaultUI: this.props.disableDefaultUI,
          zoomControl: this.props.zoomControl,
          mapTypeControl: this.props.mapTypeControl,
          scaleControl: this.props.scaleControl,
          streetViewControl: this.props.streetViewControl,
          panControl: this.props.panControl,
          rotateControl: this.props.rotateControl,
          scrollwheel: this.props.scrollwheel,
          draggable: this.props.draggable,
          keyboardShortcuts: this.props.keyboardShortcuts,
          disableDoubleClickZoom: this.props.disableDoubleClickZoom,
          noClear: this.props.noClear,
          styles: this.props.styles,
        },
      );

      Object.keys(mapConfig).forEach((key) => {
        if (!mapConfig[key]) {
          delete mapConfig[key];
        }
      });

      this.map = new google.maps.Map(mapRef, mapConfig);
      this.bounds = new window.google.maps.LatLngBounds();

      ['ready', 'click', 'dragend'].forEach((e) => {
        this.listeners[e] = this.map.addListener(e, this.handleEvent(e));
      });

      if (this.props.onMapCreated) {
        this.props.onMapCreated(mapRef);
      }
    }
  }

  handleEvent(evtName) {
    let timeout;
    const handlerName = `on${camelize(evtName)}`;

    return (e) => {
      if (timeout) {
        clearTimeout(timeout);
        timeout = null;
      }
      timeout = setTimeout(() => {
        if (this.props[handlerName]) {
          this.props[handlerName](this.props, this.map, e);
        }
      }, 0);
    };
  }

  setMapElementReference(mapElementReference) {
    this.mapElement = mapElementReference;
  }

  recenterMap() {
    const markers = this.state.markers;
    markers.forEach((marker) => {
      this.bounds.extend(marker.getPosition());
    });
    this.map.fitBounds(bounds);
  }

  mapsCallback() {
    this.setState({
      isMapCreated: true,
    });
    this.createMap();
  }

  restyleMap() {
    if (this.map) {
      window.google.maps.event.trigger(this.map, 'resize');
    }
  }

  addMarker(newMarker) {
    const markers = this.state.markes || [];
    markers.push(newMarker);
    this.setState({
      markers,
    });
    this.bounds.extend(newMarker.position);
    this.map.fitBounds(this.bounds);
  }

  renderChildren() {
    this.bounds = new window.google.maps.LatLngBounds();
    return React.Children.map(this.props.children, (child) => {
      if (!React.isValidElement(child)) {
        return child;
      }
      return React.cloneElement(child, {
        map: this.map,
        onAddMarker: this.addMarker.bind(this),
      });
    });
  }

  render() {
    const style = Object.assign({}, mapStyles.map, this.props.style, {
      display: this.props.visible ? 'inherit' : 'none',
    });

    const containerStyles = Object.assign({}, mapStyles.container, this.props.containerStyle);

    return (
      <div style={containerStyles} className={this.props.className}>
        <div
          style={style}
          ref={(map) => {
            this.setMapElementReference(map);
          }}
        >
          Loading map...
          {this.state.isMapCreated ? this.renderChildren() : null}
        </div>
      </div>
    );
  }
}

GMap.propTypes = {
  params: PropTypes.object,
  zoom: PropTypes.number,
  className: PropTypes.string,
  style: PropTypes.object,
  containerStyle: PropTypes.object,
  visible: PropTypes.bool,
  mapType: PropTypes.string,
  maxZoom: PropTypes.number,
  clickableIcons: PropTypes.bool,
  disableDefaultUI: PropTypes.bool,
  zoomControl: PropTypes.bool,
  mapTypeControl: PropTypes.bool,
  scaleControl: PropTypes.bool,
  streetViewControl: PropTypes.bool,
  panControl: PropTypes.bool,
  rotateControl: PropTypes.bool,
  scrollwheel: PropTypes.bool,
  draggable: PropTypes.bool,
  keyboardShortcuts: PropTypes.bool,
  disableDoubleClickZoom: PropTypes.bool,
  noClear: PropTypes.bool,
  styles: PropTypes.array,
  centerAroundCurrentLocation: PropTypes.number,
  onMapCreated: PropTypes.func,
  ready: PropTypes.func,
  click: PropTypes.func,
  dragend: PropTypes.func,
};

GMap.defaultProps = {
  zoom: 14,
  initialCenter: {
    lat: 37.774929,
    lng: -122.419416,
  },
  center: {},
  style: {},
  containerStyle: {},
  visible: true,
};
